import { useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchVendorAdminAccountTiers } from "../../Store/Reducers/VendorAdminSlice";

export interface AccountTierItem {
  tier_id: string;
  tier_code: string;
  tier_name?: string | null;
}

export const FALLBACK_TIER_OPTIONS = [
  { label: "Tier 1", value: "T1" },
  { label: "Tier 2", value: "T2" },
  { label: "Tier 3", value: "T3" },
];

export const formatTierLabel = (
  code: string | null | undefined,
  tiers: AccountTierItem[]
): string => {
  if (!code) return "";
  const normalized = code.toUpperCase();
  const match = tiers.find(
    tier => tier.tier_code?.toUpperCase() === normalized
  );
  if (match?.tier_name) {
    return match.tier_name;
  }
  const fallback = FALLBACK_TIER_OPTIONS.find(
    option => option.value === normalized
  );
  return fallback?.label || code;
};

export const buildTierOptions = (tiers: AccountTierItem[]) =>
  tiers.map(tier => ({
    label: tier.tier_name || tier.tier_code,
    value: tier.tier_code,
  }));

export const getTierBadgeClasses = (
  tier: string | null | undefined
): string => {
  const tierUpper = tier?.toUpperCase() || "";
  if (tierUpper.includes("T1")) {
    return "bg-blue-100 text-blue-800 border border-blue-300";
  }
  if (tierUpper.includes("T2")) {
    return "bg-purple-100 text-purple-800 border border-purple-300";
  }
  if (tierUpper.includes("T3")) {
    return "bg-gray-100 text-gray-800 border border-gray-300";
  }
  return "bg-gray-100 text-gray-800 border border-gray-300";
};

/** Base layout classes so tier labels (e.g. Tier 1) stay on one line in tight rows. */
export const tierBadgeLayoutClasses =
  "inline-flex items-center shrink-0 whitespace-nowrap leading-none";

export function useAccountTiers() {
  const dispatch = useAppDispatch();
  const { accountTiers, accountTiersLoading } = useAppSelector(
    (state: any) => state.vendorAdmin
  );

  useEffect(() => {
    if (!accountTiers?.items?.length) {
      dispatch(fetchVendorAdminAccountTiers());
    }
  }, [dispatch, accountTiers?.items?.length]);

  const items: AccountTierItem[] = accountTiers?.items ?? [];

  const tierOptions = useMemo(() => {
    if (items.length > 0) {
      return buildTierOptions(items);
    }
    return FALLBACK_TIER_OPTIONS;
  }, [items]);

  const formatTier = (code: string | null | undefined) =>
    formatTierLabel(code, items);

  return {
    tiers: items,
    tierOptions,
    formatTier,
    loading: accountTiersLoading,
  };
}
